import { useMemo, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { sumarDias } from '@abastece/shared';
import type { Producto } from '@abastece/core';
import { db } from '../db/dexie.js';
import { buscarEntre, unidadesDe } from '../lib/catalogo.js';
import { useHoy } from '../lib/hoy.js';
import { useLectorUsb } from '../lib/lector-usb.js';
import { DIAS_DE_PAQUETES, VECES_PAQUETE, claveDePaquete, paquetesSugeridos, type PaqueteSugerido } from '../lib/paquetes.js';
import { AgregarPaquete } from '../ui/AgregarPaquete.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { Ico } from '../ui/Iconos.js';
import { Miniatura } from '../ui/Miniatura.js';
import { useNombreProducto } from '../i18n/productos.js';
import { useT, type Clave } from '../i18n/index.js';
import { cantidadCorta, etiquetaPresentacion } from '../i18n/dominio.js';

type Filtro = 'sugeridos' | 'todos';

/**
 * The suggestions turned down on this device. Kept here and not in the books: turning one down
 * records nothing about the shop, and a setting every phone shares would be a change to what the
 * books hold, which every phone then replays the whole log for. Another device still suggests it.
 */
const CLAVE_NO_SUGERIDOS = 'abastece.paquetes.noSugeridos';

function leerNoSugeridos(): Set<string> {
  try {
    const guardado: unknown = JSON.parse(localStorage.getItem(CLAVE_NO_SUGERIDOS) ?? '[]');
    return new Set(Array.isArray(guardado) ? guardado.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

function recordarNoSugeridos(claves: ReadonlySet<string>) {
  try {
    localStorage.setItem(CLAVE_NO_SUGERIDOS, JSON.stringify([...claves]));
  } catch {
    // A device that keeps nothing suggests it again next time: nothing worse.
  }
}

/** What was done to a row on this visit. It stays so until another view is picked. */
type Hecho = { tipo: 'agregado'; etiqueta: string; sugerido?: PaqueteSugerido } | { tipo: 'no'; sugerido: PaqueteSugerido };

/**
 * The pack sizes the sales ask for (the owner, 2026-10-21: «can we also get a tab called "Packages"
 * that suggest missing pack sizes for items that have demonstrated a pattern of selling in specific
 * larger quantities?»).
 *
 * As «Precios/Costos» lists what has no price and «Fotos» what has no photo, this lists each product
 * that keeps being sold in one larger amount it has no package of, the amount, and the sales that
 * say so (`lib/paquetes.ts`): «12 de una vez en 10 de sus 22 ventas, en 7 días distintos». «Agregar
 * Paquete (12)» opens the package, its name, what it holds and its prices filled in, to add it
 * (`AgregarPaquete`); «No» puts the suggestion aside on this device, and «Deshacer» brings it back.
 * The most sold so first, as the list is meant to be gone down. A row done stays where it is until
 * another view is picked, as on the other two.
 *
 * «Todos» is every product on sale with the packages it has, to add one by hand where no sale has
 * asked for it yet: the box that just came in.
 *
 * A tab of «Productos», for whoever adds a presentation on a product's page: the owner, and a
 * cashier, whose package waits for the owner's prices. The search finds a product by its code too,
 * a USB scanner's included, as on «Productos».
 */
export function Paquetes() {
  const t = useT();
  const nombre = useNombreProducto();
  const hoyEs = useHoy();
  const productos = useLiveQuery(() => db.productos.toArray(), [], []);
  const unidades = useLiveQuery(() => db.unidades.toArray(), [], []);
  /** The window's sales only, by the date index: a quarter of them, not every sale the shop has made. */
  const ventas = useLiveQuery(
    () => db.ventas.where('fecha').aboveOrEqual(sumarDias(hoyEs, -(DIAS_DE_PAQUETES - 1))).toArray(),
    [hoyEs],
  );

  const [filtro, setFiltro] = useState<Filtro>('sugeridos');
  const [consulta, setConsulta] = useState('');
  const [noSugeridos, setNoSugeridos] = useState<ReadonlySet<string>>(leerNoSugeridos);
  const [hechos, setHechos] = useState<ReadonlyMap<string, Hecho>>(() => new Map());
  /** The product whose package is being added, and what its sales found. */
  const [abierto, setAbierto] = useState<{ producto: Producto; sugerido?: PaqueteSugerido } | null>(null);
  /** The search box, where whatever a USB scanner types on this screen is read (`useLectorUsb`). */
  const caja = useRef<HTMLInputElement>(null);
  useLectorUsb(caja, setConsulta);

  const activos = useMemo(() => productos.filter((p) => p.activo), [productos]);
  const sugeridos = useMemo(
    () => (ventas ? paquetesSugeridos({ productos, unidades, ventas, hoy: hoyEs, noSugeridos }) : []),
    [productos, unidades, ventas, hoyEs, noSugeridos],
  );
  const sugeridoDe = useMemo(() => new Map(sugeridos.map((s) => [s.productoId, s])), [sugeridos]);
  const filas = useMemo(() => {
    const deLaVista = filtro === 'todos' ? activos : activos.filter((p) => sugeridoDe.has(p.id) || hechos.has(p.id));
    const encontrados = buscarEntre(deLaVista, consulta, Infinity, nombre, unidades);
    if (filtro === 'todos' || consulta.trim()) return encontrados;
    // Sold so most often first; a row done keeps the place its suggestion gave it. Alphabetical among equals.
    const de = (p: Producto) => hechos.get(p.id)?.sugerido ?? sugeridoDe.get(p.id);
    return [...encontrados].sort((a, b) => (de(b)?.ventas ?? 0) - (de(a)?.ventas ?? 0) || (de(b)?.dias ?? 0) - (de(a)?.dias ?? 0));
  }, [filtro, activos, sugeridoDe, hechos, consulta, nombre, unidades]);

  /** Each view of what is lacking says how many lack it, as «Sin foto (216)» does: as the sales have it now. */
  const filtros: readonly { vista: Filtro; etiqueta: Clave; cuantos?: number }[] = [
    { vista: 'sugeridos', etiqueta: 'paquetes.sugeridos', cuantos: sugeridos.length },
    { vista: 'todos', etiqueta: 'paquetes.todos' },
  ];

  /** «Paquete (12)», «Arroba (25 libra)»: the suggestion as the till will name it. */
  const etiquetaDe = (p: Producto, s: PaqueteSugerido) => etiquetaPresentacion({ nombre: s.nombre, factor: s.factor }, p.unidadBase);
  /** «12 de una vez en 10 de sus 22 ventas, en 7 días distintos». */
  const porque = (p: Producto, s: PaqueteSugerido) =>
    t(s.ventas === s.deTodas ? 'paquetes.porqueTodas' : 'paquetes.porque', {
      cantidad: cantidadCorta(s.factor, p.unidadBase),
      veces: s.ventas,
      de: s.deTodas,
      dias: s.dias,
    });

  /** Another view: what was done on this one goes where it now belongs. */
  function verOtra(v: Filtro) {
    setFiltro(v);
    setHechos(new Map());
  }

  function hecho(productoId: string, h: Hecho | null) {
    setHechos((antes) => {
      const despues = new Map(antes);
      if (h) despues.set(productoId, h);
      else despues.delete(productoId);
      return despues;
    });
  }

  /** «No»: put aside on this device, the row kept with its «Deshacer». */
  function noSugerir(productoId: string, s: PaqueteSugerido, poner: boolean) {
    const claves = new Set(noSugeridos);
    if (poner) claves.add(claveDePaquete(productoId, s.factor));
    else claves.delete(claveDePaquete(productoId, s.factor));
    setNoSugeridos(claves);
    recordarNoSugeridos(claves);
    hecho(productoId, poner ? { tipo: 'no', sugerido: s } : null);
  }

  if (activos.length === 0) {
    return (
      <Pagina titulo={t('paquetes.titulo')}>
        <Vacio mensaje={t('paquetes.nadaQueMostrar')}>
          <Link to="/productos/semilla" className="btn-primario px-5">
            <Ico icono="semilla" />
            {t('productos.empezarCatalogo')}
          </Link>
        </Vacio>
      </Pagina>
    );
  }

  return (
    <Pagina titulo={t('paquetes.titulo')} subtitulo={t('paquetes.subtitulo', { dias: DIAS_DE_PAQUETES })}>
      <p className="mb-4 rounded-xl bg-slate-900 px-4 py-3 text-xs text-slate-400">
        {t('paquetes.explicacion', { dias: DIAS_DE_PAQUETES, veces: VECES_PAQUETE })}
      </p>

      {/* Under the search on a phone, where «Missing Packages (47)» beside it leaves the box no room. */}
      <div className="mb-4 flex flex-col gap-2 sm:flex-row">
        <input
          ref={caja}
          className="campo min-w-0 flex-1"
          placeholder={t('paquetes.buscar')}
          value={consulta}
          onChange={(e) => setConsulta(e.target.value)}
          autoComplete="off"
          inputMode="search"
        />
        <div className="flex shrink-0 gap-1 rounded-xl bg-slate-800 p-1">
          {filtros.map(({ vista, etiqueta, cuantos }) => (
            <button
              key={vista}
              type="button"
              onClick={() => verOtra(vista)}
              aria-pressed={filtro === vista}
              data-paquetes-filtro={vista}
              data-sin-icono="pestana"
              // Too narrow for both on one line, the count goes under its name, never into it.
              className={`min-h-10 flex-auto rounded-lg px-2.5 py-1 text-sm leading-tight transition max-[359px]:px-2 ${
                filtro === vista ? 'bg-marca-600 font-medium text-white' : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
              }`}
            >
              <span className="whitespace-nowrap">{t(etiqueta)}</span>
              {cuantos !== undefined && (
                <>
                  {' '}
                  <span className="tabular whitespace-nowrap">({cuantos})</span>
                </>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* The sales are read a moment after the products: nothing is said of them until they are. */}
      {ventas === undefined ? null : filas.length === 0 ? (
        consulta.trim() ? (
          <Vacio mensaje={t(filtro === 'sugeridos' ? 'paquetes.ningunoSugerido' : 'paquetes.ninguno')} icono="productos" />
        ) : (
          <Vacio mensaje={t('paquetes.nadaQueSugerir')}>
            <Link to="/" className="btn-primario px-5">
              <Ico icono="listo" />
              {t('paquetes.seguir')}
            </Link>
          </Vacio>
        )
      ) : (
        <ul className="flex flex-col gap-2">
          {filas.map((p) => {
            const h = hechos.get(p.id);
            const s = sugeridoDe.get(p.id);
            return (
              <li
                key={p.id}
                // The buttons under the name on a phone, beside it where there is room.
                className="tarjeta flex flex-wrap items-center gap-3 p-3"
                data-paquetes-producto={p.id}
                data-paquetes-estado={h?.tipo ?? (s ? 'sugerido' : 'nada')}
              >
                <Miniatura producto={p} tamano="lg" />
                <div className="min-w-0 flex-1 basis-40">
                  <p className="truncate font-medium">{nombre(p)}</p>
                  {/* What it has, on «Todos»: under «Sin paquete» every row has only its unit. */}
                  {filtro === 'todos' && (
                    <p className="mt-0.5 truncate text-xs text-slate-500" data-paquetes-tiene>
                      {unidadesDe(unidades, p.id)
                        .map((u) => etiquetaPresentacion(u, p.unidadBase))
                        .join(' · ')}
                    </p>
                  )}
                  {h?.tipo === 'agregado' ? (
                    <p className="mt-0.5 flex items-center gap-1 text-xs text-teal-300" data-paquetes-hecho>
                      <Ico icono="listo" className="size-3.5 shrink-0" />
                      <span className="min-w-0">{t('paquetes.agregado', { paquete: h.etiqueta })}</span>
                    </p>
                  ) : h?.tipo === 'no' ? (
                    <p className="mt-0.5 text-xs text-slate-400" data-paquetes-hecho>
                      {t('paquetes.yaNo', { paquete: etiquetaDe(p, h.sugerido) })}
                    </p>
                  ) : (
                    // The package named on its row, and why: its button has room for «Agregar» at 320 px.
                    s && (
                      <p className="mt-0.5 text-xs text-slate-400">
                        <span className="font-medium text-teal-300" data-paquetes-sugerido>
                          {etiquetaDe(p, s)}
                        </span>
                        {' · '}
                        <span data-paquetes-porque>{porque(p, s)}</span>
                      </p>
                    )
                  )}
                </div>
                <div className="flex w-full gap-2 sm:w-auto">
                  {h?.tipo === 'no' ? (
                    <button
                      type="button"
                      className="btn-secundario h-11 min-h-0 flex-1 gap-1.5 px-4 text-sm whitespace-nowrap sm:flex-none"
                      onClick={() => noSugerir(p.id, h.sugerido, false)}
                      data-paquetes-deshacer={p.id}
                    >
                      <Ico icono="devolver" className="size-4" />
                      {t('paquetes.deshacer')}
                    </button>
                  ) : s && h?.tipo !== 'agregado' ? (
                    <>
                      <button
                        type="button"
                        className="btn-secundario h-11 min-h-0 flex-1 gap-1.5 px-4 text-sm whitespace-nowrap sm:flex-none"
                        onClick={() => setAbierto({ producto: p, sugerido: s })}
                        aria-label={t('paquetes.agregarDe', { paquete: etiquetaDe(p, s), producto: nombre(p) })}
                        data-paquetes-agregar={p.id}
                      >
                        <Ico icono="agregar" className="size-4" />
                        {t('comun.agregar')}
                      </button>
                      <button
                        type="button"
                        className="btn-secundario h-11 min-h-0 gap-1.5 px-4 text-sm whitespace-nowrap"
                        onClick={() => noSugerir(p.id, s, true)}
                        aria-label={t('paquetes.noDe', { paquete: etiquetaDe(p, s), producto: nombre(p) })}
                        title={t('paquetes.noExplicado')}
                        data-paquetes-no={p.id}
                      >
                        <Ico icono="cancelar" className="size-4" />
                        {t('comun.no')}
                      </button>
                    </>
                  ) : (
                    // Another package, by hand: on «Todos», and on a row whose suggestion was just added.
                    <button
                      type="button"
                      className="btn-secundario h-11 min-h-0 flex-1 gap-1.5 px-4 text-sm whitespace-nowrap sm:flex-none"
                      onClick={() => setAbierto({ producto: p })}
                      aria-label={t('paquetes.nuevoDe', { producto: nombre(p) })}
                      data-paquetes-nuevo={p.id}
                    >
                      <Ico icono="nuevo" className="size-4" />
                      {t('paquetes.nuevo')}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {abierto && (
        <AgregarPaquete
          producto={abierto.producto}
          {...(abierto.sugerido ? { sugerido: abierto.sugerido, porque: porque(abierto.producto, abierto.sugerido) } : {})}
          onAgregado={(etiqueta) => {
            // A second one added by hand keeps the row where the first one's suggestion put it.
            const sugerido = abierto.sugerido ?? hechos.get(abierto.producto.id)?.sugerido;
            hecho(abierto.producto.id, { tipo: 'agregado', etiqueta, ...(sugerido ? { sugerido } : {}) });
          }}
          onCerrar={() => setAbierto(null)}
        />
      )}
    </Pagina>
  );
}
